import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import history from 'history.js';
import { useAtomValue } from 'jotai';
import React, { useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Card, CardHeader, CardBody, CardTitle } from 'reactstrap';

import { getDrillDownUrl } from './helper';
import {
  appliedHostIdsAtom,
  criticalityFiltersAtom,
  policyIdFilterAtom,
} from './state';
import { getSeverityFilterValues } from '../helper';

ChartJS.register(ArcElement, Tooltip, Legend);

const ComplianceChart = ({ passed, failed }) => {
  const policyId = useAtomValue(policyIdFilterAtom);
  const appliedHostIds = useAtomValue(appliedHostIdsAtom);

  const severityFilters = useAtomValue(criticalityFiltersAtom);
  const total = passed + failed;
  const pieChartReference = useRef<unknown>();

  const data = {
    labels: ['Passed', 'Failed'],
    datasets: [
      {
        data: [passed, failed],
        backgroundColor: ['#5FB3B3', '#B85050'],
        // Hover colors adjusted to harmonize with the new palette
        hoverBackgroundColor: ['#66CFCF', '#D16262'],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '20%',
    hoverBorderWidth: 2,
    hoverOffset: 3,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            const percentage = ((value / total) * 100).toFixed(2);
            return `${tooltipItem.label}: ${value} (${percentage}%)`;
          },
        },
        backgroundColor: '#f5f5f5',
        titleColor: '#333',
        bodyColor: '#666',
        bodySpacing: 4,
        padding: 12,
      },
      legend: {
        display: true,
        position: 'right' as const,
      },
      datalabels: {
        color: 'black',
        borderColor: 'white',
        borderRadius: 5,
        backgroundColor: 'white',
        font: {
          weight: 'bold' as const,
        },
        padding: 1,
        anchor: 'end' as const,
        align: 'end' as const,
        offset: 0,
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: 'white',
      },
    },
  };

  function handleChartClick(
    event: React.MouseEvent<HTMLCanvasElement, MouseEvent>,
    chart,
    labels: string[],
  ): void {
    // Chart.js v3.9.1 with react-chartjs-2 v3.0.4,
    if (!chart) return;
    const activeElements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false,
    );

    if (activeElements?.length > 0) {
      const activeElement = activeElements[0];
      const dataIndex = activeElement.index;

      const label = labels[dataIndex];
      if (label) {
        const url = getDrillDownUrl({
          policyId: policyId,
          hosts_in: appliedHostIds,
          status_in: [label],
          criticality_in: getSeverityFilterValues(severityFilters),
        });
        if (url) history.push(url);
      }
    }
  }

  return (
    <Card className="card-chart border border-light">
      <CardHeader>
        <CardTitle tag="div" className="mb-0">
          <div className="d-flex flex-row align-items-baseline">
            <h1 className="d-inline mb-0">
              {total > 0 ? ((passed / total) * 100).toFixed(1) : '0.0'}
            </h1>
            <h5 className="card-category px-1">% Compliance</h5>
          </div>
        </CardTitle>
      </CardHeader>
      <CardBody style={{ cursor: 'pointer' }}>
        <div className="chart-area">
          <Doughnut
            ref={(node) => {
              if (node != null) pieChartReference.current = node;
            }}
            data={data}
            options={options}
            onClick={(event) =>
              handleChartClick(event, pieChartReference.current, data.labels)
            }
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default ComplianceChart;
